import { Link } from "react-router-dom";
import { ScrollReveal } from "@/components/base/ScrollAnimations";
import { piicCells, piicServices } from "@/mocks/piicData";

const serviceIcons = [
  "ri-user-search-line",
  "ri-briefcase-5-line",
  "ri-team-line",
  "ri-hand-heart-line",
  "ri-microscope-line",
  "ri-bar-chart-box-line",
  "ri-shield-check-line",
  "ri-exchange-line",
];

const cellRoutes: Record<string, string> = {
  "Textile Engineering Cell": "/cells/textile-engineering",
  "Management Excellence Cell": "/cells/management-excellence",
  "Textile Processing Cell": "/cells/textile-processing",
  "Textile Materials Cell": "/cells/textile-materials",
  "Marketing & Merchandising Cell": "/cells/marketing-merchandising",
  "Textile Efficiency Cell": "/cells/textile-efficiency",
};

export default function PiicCells() {
  return (
    <section className="bg-white py-16 md:py-24 lg:py-32">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-tie-teal/10 mb-4 md:mb-6">
                <span className="text-xs font-semibold text-tie-teal uppercase tracking-wider">
                  / Innovation Cells
                </span>
              </div>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-tie-text-primary leading-tight">
                Six Specialised Cells. One Integrated Approach.
              </h2>
              <p className="mt-3 md:mt-4 text-base md:text-lg text-tie-text-secondary">
                Each Innovation Circle draws expertise from one of six specialised cells, ensuring that solutions are technically sound, managerially feasible, and commercially viable.
              </p>
            </div>
          </ScrollReveal>

          {/* Cells Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {piicCells.map((cell, index) => {
              const route = cellRoutes[cell.name];
              const CellCard = (
                <div className="group h-full p-5 md:p-6 rounded-xl bg-tie-grey-light border border-tie-grey-dark hover:border-tie-teal/30 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-center gap-3 mb-4 md:mb-5">
                    <div className="w-10 h-10 rounded-lg bg-tie-teal flex items-center justify-center shrink-0">
                      <i className={`${cell.icon} text-white text-base`} />
                    </div>
                    <h3 className="text-base md:text-lg font-semibold text-tie-text-primary">
                      {cell.name}
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {cell.focus.map((item) => (
                      <div
                        key={item}
                        className="flex items-start gap-2.5 text-sm text-tie-text-secondary"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-tie-teal shrink-0 mt-1.5" />
                        {item}
                      </div>
                    ))}
                  </div>
                  {route && (
                    <div className="mt-4 pt-4 border-t border-tie-grey-dark">
                      <span className="inline-flex items-center text-sm font-medium text-tie-teal group-hover:text-tie-teal-dark transition-colors">
                        Explore cell
                        <i className="ri-arrow-right-line ml-1.5 text-xs group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  )}
                </div>
              );

              return (
                <ScrollReveal key={cell.name} delay={index * 100}>
                  {route ? (
                    <Link to={route} className="block cursor-pointer">
                      {CellCard}
                    </Link>
                  ) : (
                    CellCard
                  )}
                </ScrollReveal>
              );
            })}
          </div>

          {/* Services & Activities of Each Cell */}
          <ScrollReveal delay={200}>
            <div className="mt-16 md:mt-24">
              <div className="text-center max-w-3xl mx-auto mb-8 md:mb-12">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-tie-navy/10 mb-4 md:mb-5">
                  <span className="text-xs font-semibold text-tie-navy uppercase tracking-wider">
                    / What Every Cell Delivers
                  </span>
                </div>
                <h3 className="font-serif text-xl md:text-2xl lg:text-3xl font-bold text-tie-text-primary leading-tight">
                  Services and Activities of Each Cell
                </h3>
                <p className="mt-3 md:mt-4 text-base md:text-lg text-tie-text-secondary">
                  Beyond technical specialisation, every cell delivers a consistent suite of services that drive capability building, knowledge exchange, and operational excellence across the ecosystem.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {piicServices.map((service, index) => (
                  <div
                    key={service}
                    className="group p-4 md:p-5 rounded-xl bg-tie-grey-light border border-tie-grey-dark hover:border-tie-navy/30 hover:shadow-md transition-all duration-300"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-tie-navy/10 flex items-center justify-center shrink-0 group-hover:bg-tie-navy group-hover:text-white transition-colors duration-300">
                        <i className={`${serviceIcons[index]} text-tie-navy text-sm group-hover:text-white transition-colors duration-300`} />
                      </div>
                      <p className="text-sm font-medium text-tie-text-primary leading-snug pt-1.5">
                        {service}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}